import { toSafeInternalPath } from '@/utils/hard-redirect';

/** The query params the reset and invite emails put on the link. */
export type NewPasswordSearch = {
  resetPasswordToken?: string;
  /** `invite` when the link comes from a team invite rather than a password reset. */
  source?: string;
  inviteType?: string;
  /** Where to land once the password is set. Only a same-origin app path is honoured. */
  origin?: string;
};

/**
 * TanStack runs each value through `JSON.parse`, so a token made only of digits arrives as a
 * number. Accept that form rather than dropping the param, which would strip it from the URL.
 */
function asSearchString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value === '' ? undefined : value;
  }
  return typeof value === 'number' ? String(value) : undefined;
}

/** The params as they come off the URL, before parsing. */
type RawNewPasswordSearch = {
  resetPasswordToken?: unknown;
  source?: unknown;
  inviteType?: unknown;
  origin?: unknown;
};

export function parseNewPasswordSearch(search: RawNewPasswordSearch): NewPasswordSearch {
  return {
    resetPasswordToken: asSearchString(search.resetPasswordToken),
    source: asSearchString(search.source),
    inviteType: asSearchString(search.inviteType),
    origin: asSearchString(search.origin),
  };
}

/** The `/login` params that carry an invite through the TOTP step. */
export type LoginHandoffSearch = {
  origin?: string;
  invite_type?: string;
};

/** Login only receives a URL, so the invite params travel to it on the query string. */
export function loginSearchAfterPasswordSet({ origin, inviteType }: NewPasswordSearch): LoginHandoffSearch {
  const search: LoginHandoffSearch = {};
  const safeOrigin = toSafeInternalPath(origin);
  if (safeOrigin !== undefined) {
    search.origin = safeOrigin;
  }
  if (inviteType !== undefined) {
    search.invite_type = inviteType;
  }
  return search;
}
